import { Alert, Button, Checkbox, Descriptions, Form, Input, Select, Space, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { TWO_COLUMNS } from '@/shared/ui/descriptionColumns'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { REASON_MAX } from './cashdeskApi'
import { percentOf, type Discount, type SaleQuote, type SaleState } from './salesApi'

interface SaleSectionProps {
  quote: SaleQuote | undefined
  quoteLoading: boolean
  quoteError: unknown
  state: SaleState
  discount: number
  discountId: string | null
  approved: readonly Discount[]
  canApplyDiscount: boolean
  currency: string
  onPickApproved: (discount: Discount | null) => void
  onRequestApproval: () => void
  requesting: boolean
  requestError: unknown
  requested: boolean
}

export function SaleSection({
  quote,
  quoteLoading,
  quoteError,
  state,
  discount,
  discountId,
  approved,
  canApplyDiscount,
  currency,
  onPickApproved,
  onRequestApproval,
  requesting,
  requestError,
  requested,
}: SaleSectionProps) {
  const { t } = useTranslation()

  if (quoteLoading) return <Spin style={{ display: 'block', marginBottom: 16 }} />
  if (quoteError) {
    return <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(quoteError, t)} />
  }
  if (!quote || !state.preview) return null

  const m = (amount: number): Money => money(amount, quote.price.currency)
  const { preview } = state
  const usable = approved.filter((d) => d.planId === quote.planId)
  const discountAllowed = canApplyDiscount && quote.canApplyDiscount

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
      <Descriptions size="small" column={{ xs: 1, sm: 3 }} bordered>
        <Descriptions.Item label={t('sales.price')}>{formatMoneyValue(quote.price)}</Descriptions.Item>
        <Descriptions.Item label={t('sales.balanceNow')}>{formatMoneyValue(quote.balance)}</Descriptions.Item>
        <Descriptions.Item label={t('sales.creditLimit')}>{formatMoneyValue(quote.debtLimit)}</Descriptions.Item>
      </Descriptions>

      {discountAllowed ? (
        <>
          {usable.length > 0 ? (
            <Form.Item name="discountId" label={t('sales.approvedDiscount')} style={{ marginBottom: 0 }}>
              <Select<string>
                allowClear
                placeholder={t('sales.approvedDiscountPlaceholder')}
                onChange={(id) => onPickApproved(usable.find((d) => d.id === id) ?? null)}
                options={usable.map((d) => ({
                  value: d.id,
                  label: `${formatMoneyValue(d.amount)} — ${d.decidedBy ? d.decidedBy.name : t('common.dash')}`,
                }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item
            name="discount"
            label={t('sales.discount')}
            extra={discount > 0 ? t('sales.discountPercent', { percent: (percentOf(discount, quote.price.amount) / 100).toFixed(2) }) : undefined}
            style={{ marginBottom: 0 }}
            rules={[
              {
                validator: (_, value: Money | null | undefined) =>
                  !value || value.amount <= quote.price.amount
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('sales.discountTooLarge'))),
              },
            ]}
          >
            <MoneyInput currency={currency} minMinor={0} disabled={discountId !== null} style={{ width: '100%' }} />
          </Form.Item>
          {discount > 0 ? (
            <Form.Item
              name="discountReason"
              label={t('sales.discountReason')}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true, whitespace: true, message: t('sales.discountReasonRequired') },
                { max: REASON_MAX, message: t('cashdesk.reasonTooLong') },
              ]}
            >
              <Input.TextArea rows={2} maxLength={REASON_MAX} disabled={discountId !== null} />
            </Form.Item>
          ) : null}
          {state.verdict === 'WITHIN' ? (
            <Alert
              type="info"
              showIcon
              message={discountId ? t('sales.discountPreApproved') : t('sales.discountWithinLimit')}
            />
          ) : null}
          {state.verdict === 'APPROVAL' ? (
            <Alert
              type="warning"
              showIcon
              message={t('sales.discountNeedsApproval')}
              description={
                requested ? (
                  t('sales.discountRequested')
                ) : (
                  <Button loading={requesting} onClick={onRequestApproval}>
                    {t('sales.requestApproval')}
                  </Button>
                )
              }
            />
          ) : null}
          {state.verdict === 'FORBIDDEN' ? (
            <Alert type="error" showIcon message={t('sales.discountForbidden')} />
          ) : null}
          {requestError ? <Alert type="error" showIcon message={translateError(requestError, t)} /> : null}
        </>
      ) : null}

      <Descriptions size="small" column={TWO_COLUMNS} bordered>
        <Descriptions.Item label={t('sales.balanceAfter')}>{formatMoneyValue(m(preview.balanceAfter))}</Descriptions.Item>
        <Descriptions.Item label={t('sales.outstanding')}>{formatMoneyValue(m(preview.outstanding))}</Descriptions.Item>
      </Descriptions>

      {preview.debtIncrease > 0 ? (
        preview.overLimit ? (
          <Alert
            type="error"
            showIcon
            message={t('sales.creditLimitExceeded', {
              debt: formatMoneyValue(m(preview.outstanding)),
              limit: formatMoneyValue(quote.debtLimit),
            })}
          />
        ) : quote.canSellOnCredit ? (
          <Alert
            type="warning"
            showIcon
            message={t('sales.creditWarning', { amount: formatMoneyValue(m(preview.debtIncrease)) })}
            description={
              <Space direction="vertical">
                <Form.Item name="allowCredit" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>{t('sales.allowCredit')}</Checkbox>
                </Form.Item>
              </Space>
            }
          />
        ) : (
          <Alert type="error" showIcon message={t('sales.creditForbidden')} />
        )
      ) : null}
    </Space>
  )
}
